import { ProdantixClient } from 'ProdantixSdk/core/client';
import { namespaceFor } from 'ProdantixSdk/core/config';
import { webStorage } from 'ProdantixSdk/core/storage';
import { systemClock } from 'ProdantixSdk/core/time';
import type { ProdantixConfig, StorageAdapter } from 'ProdantixSdk/core/types';
import { ACTION_ATTRIBUTE, ANNOTATION_ATTRIBUTE, ANNOTATION_MAX_LENGTH, describeElement, IGNORE_ATTRIBUTE, reportOnce } from 'ProdantixSdk/web/annotation';
import { type AutocaptureOptions, installAutocapture } from 'ProdantixSdk/web/autocapture';
import { WebTransport } from 'ProdantixSdk/web/beacon';
import { watchConsent } from 'ProdantixSdk/web/consent/apply';
import { createCookieStorage } from 'ProdantixSdk/web/cookie-storage';
import { installEngage } from 'ProdantixSdk/web/engage/install';
import { adoptCarry, type DocumentLike, type HistoryLike, installLinkCarry, type LocationLike } from 'ProdantixSdk/web/link-carry';
import { installSessionReplay, type SessionReplayOptions } from 'ProdantixSdk/web/replay/index';

export type { Consent } from 'ProdantixSdk/core/types';
export type { CookieJar, CookieStorageOptions } from 'ProdantixSdk/web/cookie-storage';
export { createCookieStorage, resolveCookieDomain } from 'ProdantixSdk/web/cookie-storage';
export type { CtasHost, RenderCtaOptions, RenderedCta, RunCtasOptions } from 'ProdantixSdk/web/ctas/index';
export { ctasHost, deviceOf, flagAllows, renderCta, runCtas, startCtas } from 'ProdantixSdk/web/ctas/index';
export type { FormsHost, PopupRules, PopupTrigger, RenderedForm, RenderFormOptions } from 'ProdantixSdk/web/forms/index';
export { formLanguage, formsHost, renderForm, watchPopup } from 'ProdantixSdk/web/forms/index';
export type { Carry } from 'ProdantixSdk/web/link-carry';
export { decorateLink, installLinkCarry, readCarry, stripCarry } from 'ProdantixSdk/web/link-carry';
export type { PushBrowser, PushDevice, PushPermission, PushPlatform, PushRegistration, RegisterPushOptions } from 'ProdantixSdk/web/push/index';
export { DEFAULT_SERVICE_WORKER, PushClient, registerPush } from 'ProdantixSdk/web/push/index';
export type { MaskOptions, SessionReplayOptions } from 'ProdantixSdk/web/replay/index';
export type { AutocaptureOptions };
export { ACTION_ATTRIBUTE, ANNOTATION_ATTRIBUTE, ANNOTATION_MAX_LENGTH, IGNORE_ATTRIBUTE, installAutocapture, installSessionReplay, WebTransport };

/** Tunables for the built-in session replay, minus the identity fields
 *  `createWebClient` fills in (apiKey, distinctId, replayHost). */
export type WebSessionReplayOptions = Pick<SessionReplayOptions, 'flushIntervalMs' | 'mask' | 'maxEvents' | 'sessionId'>;

export interface WebClientOptions extends ProdantixConfig {
  autocaptureOptions?: AutocaptureOptions;
  cookieDomain?: string;
  /** Registrable domains whose links carry the visitor's anonymous id and
   *  session in a `_pdx` query parameter, because a cookie cannot cross a
   *  registrable domain. Empty by default. Readonly so a caller hands over the
   *  shared platform list as it is, rather than copying it to satisfy a type. */
  linkedDomains?: readonly string[];
  /** Host of the recorder service (`eu.replay.prodantix.com`). Required to enable
   *  session replay. */
  replayHost?: string;
  /** Enable session replay (DOM + interaction capture, inputs masked by default).
   *  Requires `replayHost`. */
  sessionReplay?: boolean;
  surveys?: boolean;
  guides?: boolean;
  sessionReplayOptions?: WebSessionReplayOptions;
}

interface NavigatorLike {
  language?: string;
  platform?: string;
  userAgentData?: { platform?: string };
}

interface LifecycleTarget {
  addEventListener?(type: string, listener: () => void): void;
}

function getNavigator(): NavigatorLike | undefined {
  return (globalThis as { navigator?: NavigatorLike }).navigator;
}

function getDocument(): DocumentLike | undefined {
  return (globalThis as { document?: DocumentLike }).document;
}

function getHistory(): HistoryLike | undefined {
  return (globalThis as { history?: HistoryLike }).history;
}

function getLocation(): LocationLike | undefined {
  return (globalThis as { location?: LocationLike }).location;
}

function detectOs(nav: NavigatorLike | undefined): string | undefined {
  const platform = nav?.userAgentData?.platform ?? nav?.platform;
  return platform ? platform.slice(0, 50) : undefined;
}

function installLifecycle(client: ProdantixClient): void {
  const flush = (): void => {
    void client.flush();
  };
  const doc = (globalThis as { document?: LifecycleTarget }).document;
  doc?.addEventListener?.('visibilitychange', flush);
  const win = globalThis as LifecycleTarget;
  win.addEventListener?.('pagehide', flush);
}

function sharedStorageFor(apiKey: string, cookieDomain: string | undefined, local: StorageAdapter): StorageAdapter {
  try {
    return createCookieStorage({ domain: cookieDomain, name: `pdx_${namespaceFor(apiKey)}` }) ?? local;
  } catch {
    return local;
  }
}

export function createWebClient(options: WebClientOptions): ProdantixClient {
  const { autocaptureOptions, cookieDomain, guides, linkedDomains, replayHost, sessionReplay, sessionReplayOptions, surveys, ...base } = options;
  const nav = getNavigator();
  const local = webStorage();
  const storage = base.storage ?? local;
  const sharedStorage = base.sharedStorage ?? sharedStorageFor(base.apiKey, cookieDomain, local);
  const previousAnonymousId = adoptCarry({
    history: getHistory(),
    location: getLocation(),
    namespace: namespaceFor(base.apiKey),
    now: (base.clock ?? systemClock).now().getTime(),
    shared: sharedStorage,
    storage,
  });
  const report = reportOnce(base.onError ?? (() => undefined));
  const config: ProdantixConfig = {
    ...base,
    describeElement: base.describeElement ?? (element => describeElement(element, report)),
    locale: base.locale ?? (nav?.language ? nav.language.slice(0, 20) : undefined),
    os: base.os ?? detectOs(nav),
    sessionTracking: base.sessionTracking ?? true,
    sharedStorage,
    storage,
    transport:
      base.transport ??
      new WebTransport({
        maxRetries: base.maxRetries,
        requestTimeoutMs: base.requestTimeoutMs,
      }),
  };
  const client = new ProdantixClient(config);
  if (previousAnonymousId !== undefined) {
    client.alias(previousAnonymousId);
  }
  installLifecycle(client);
  if (config.consentMode === 'banner' && typeof window !== 'undefined') watchConsent(client, window);
  if (options.autocapture ?? true) {
    installAutocapture(client, { onError: config.onError, ...autocaptureOptions });
  }
  const doc = getDocument();
  if (doc && linkedDomains) {
    installLinkCarry(doc, client, linkedDomains);
  }
  const win = typeof window === 'undefined' ? undefined : window;
  if (win && (surveys !== false || guides !== false)) {
    installEngage(client, win, {
      edgeHost: (base.flagsHost ?? base.host).replace(/\/+$/, ''),
      guides: guides !== false,
      namespace: namespaceFor(base.apiKey),
      onError: config.onError ?? (() => undefined),
      storage,
      surveys: surveys !== false,
    });
  }
  if (sessionReplay && replayHost) {
    const startReplay = (): void => {
      installSessionReplay({
        apiKey: config.apiKey,
        distinctId: () => client.distinctId,
        onError: config.onError,
        replayHost,
        ...sessionReplayOptions,
        sessionId: sessionReplayOptions?.sessionId ?? client.sessionId,
      });
    };
    if (client.isCookieless()) {
      const stop = client.onStorageAllowed(() => {
        stop();
        startReplay();
      });
    } else {
      startReplay();
    }
  }
  return client;
}
